/**
 * CypSnapScalesPanel — SNAP-IV 26 subscale scores for CYP ADHD informants.
 *
 * Reads each informant's formInstances.snapIV26Informant.scaleResults (the
 * frontend scorer writes them at submit time — scoreSnapIV26) and shows the
 * three subscale scores with their severity bands.
 *
 * The chrome is the adult ADHD Patient Self-Report Scales panel's, copied
 * rather than reinvented: the same bordered divided row list, the same green
 * completion chip, the same 24-unit score bar with the same four severity
 * colours, and the same severity pill classes. A clinician who works across
 * both platforms should not have to learn this block twice.
 *
 * Two call sites share this file:
 *   - CypSnapScaleColumn: one informant's scores, no card chrome. The CYP
 *     overview puts this under a "Rating scales" heading inside each
 *     parent/carer or school card.
 *   - CypSnapScalesPanel (default): the two columns together in one card.
 *     The DD Review page still wants that combined view.
 *
 * There is no expand chevron — a subscale row has nothing to expand, and a
 * chevron that does nothing promises an interaction that is not there.
 *
 * Severity always carries its label alongside the number, so meaning never
 * rides on colour alone; an unsubmitted scale renders a quiet waiting state —
 * the school column is advisory and never blocks.
 */

import React from 'react';
import { CheckIcon, SearchIcon } from '@heroicons/react/solid';

const SUBSCALES = [
  { key: 'inattention', label: 'Inattention', max: 27 },
  { key: 'hyperactivityImpulsivity', label: 'Hyperactivity/Impulsivity', max: 27 },
  { key: 'oppositionDefiance', label: 'Opposition/Defiance', max: 24 },
];

const SNAP_TOTAL_MAX = 78;

/** Adult's severity pill classes, matched band for band. */
function severityPillClass(severity) {
  const base = 'px-2 py-1 rounded-full text-xs font-medium';
  if (!severity) return `${base} bg-gray-100 text-gray-800`;
  const lower = severity.toLowerCase();
  if (lower.includes('not clinically significant')) return `${base} bg-gray-100 text-gray-800`;
  if (lower.includes('mild')) return `${base} bg-yellow-100 text-yellow-800`;
  if (lower.includes('moderate')) return `${base} bg-orange-100 text-orange-800`;
  if (lower.includes('severe')) return `${base} bg-red-100 text-red-800`;
  return `${base} bg-gray-100 text-gray-800`;
}

/** Adult's score-bar fill colours, keyed off the band the scorer assigned. */
function severityBarColor(severity) {
  const lower = (severity || '').toLowerCase();
  if (lower.includes('not clinically significant')) return '#4ade80';
  if (lower.includes('mild')) return '#facc15';
  if (lower.includes('moderate')) return '#fb923c';
  if (lower.includes('severe')) return '#ef4444';
  return '#9ca3af';
}

function ScoreBar({ score, max, severity }) {
  const percentage = max > 0 ? Math.min((score / max) * 100, 100) : 0;
  return (
    <div className="w-24 bg-gray-200 rounded-full h-2 flex-shrink-0">
      <div
        className="h-2 rounded-full"
        style={{ width: `${percentage}%`, backgroundColor: severityBarColor(severity) }}
      />
    </div>
  );
}

/**
 * A slash is the only sensible place a long subscale name can break
 * ("Hyperactivity/" then "Impulsivity") if the card is too narrow to keep
 * it on one line. The break is a last resort: on a card with room, the
 * name stays whole.
 */
function ScalableLabel({ label }) {
  const parts = String(label).split('/');
  if (parts.length === 1) return label;
  return parts.map((part, index) => (
    <React.Fragment key={`${part}-${index}`}>
      {index > 0 ? <>/<wbr /></> : null}
      {part}
    </React.Fragment>
  ));
}

function SubscaleRow({ label, score, max, severity, isTotal, indented, actionSlot }) {
  return (
    // col-span-5 + subgrid: this row uses the list's shared columns (name,
    // bar, score, band, action) instead of sizing its own. The action column
    // holds the magnifying-glass icon on the total row and is empty elsewhere.
    <div
      data-scale-row={label}
      className="col-span-5 grid grid-cols-subgrid items-center py-3 bg-white"
    >
      <div className={`flex items-center min-w-0 ${indented ? 'pl-9' : 'pl-3'}`}>
        <div className="flex items-center justify-center h-5 w-5 rounded-full bg-green-100 flex-shrink-0">
          <CheckIcon className="h-3 w-3 text-green-600" />
        </div>
        <h3 className={`ml-2 min-w-0 ${isTotal ? 'font-semibold text-gray-900' : 'font-medium text-gray-900'}`}>
          <ScalableLabel label={label} />
        </h3>
      </div>

      <ScoreBar score={score} max={max} severity={severity} />
      <span className="text-sm font-medium text-gray-900 whitespace-nowrap tabular-nums text-right">
        {score}/{max}
      </span>
      {severity ? (
        <span className={`${severityPillClass(severity)} justify-self-start whitespace-nowrap mr-3`}>
          {severity}
        </span>
      ) : (
        <span aria-hidden="true" className="mr-3" />
      )}
      {/* Action column — magnifying glass on the total row, empty on subscales */}
      <div className="flex items-center justify-center">
        {actionSlot || null}
      </div>
    </div>
  );
}

function snapResultsFrom(view) {
  const results = view?.formInstances?.snapIV26Informant?.scaleResults;
  return results?.complete ? results : null;
}

/**
 * One informant's SNAP-IV 26 scores. The heading is optional so the overview
 * can put this under its own "Rating scales" subheading without repeating
 * "Parent/carer" or "School" a second time.
 *
 * onViewDetails — optional callback fired when the clinician clicks the
 * magnifying-glass icon on the total row. The parent wires this up to open
 * FormDetailsPanel with source: 'informant' and the right informantId.
 */
export function CypSnapScaleColumn({ heading, results, emptyText, onViewDetails }) {
  return (
    <div className="min-w-0">
      {heading ? (
        <p className="text-sm font-semibold text-gray-700 mb-2">{heading}</p>
      ) : null}
      {/* Five grid columns: name | bar | score | severity-band | action.
          The action column is narrow (2rem) and holds the magnifying glass
          on the SNAP-IV total row only. */}
      <div className="border rounded-lg divide-y grid grid-cols-[minmax(0,1fr)_6rem_2.75rem_max-content_2rem] gap-x-2">
        {results ? (
          <>
            {/* The instrument total sits first so the three subscales read as
                its parts. SNAP-IV 26 bands those subscales, not the total,
                so this row has no severity pill. */}
            <SubscaleRow
              label="SNAP-IV (26-item)"
              score={results.totalScore}
              max={SNAP_TOTAL_MAX}
              severity={null}
              isTotal
              actionSlot={onViewDetails ? (
                <div
                  className="flex items-center justify-center w-7 h-7 text-violet-800 rounded-full hover:bg-violet-100 transition-colors cursor-pointer"
                  onClick={onViewDetails}
                  title="View raw questions and answers"
                  aria-label="View SNAP-IV raw questions and answers"
                >
                  <SearchIcon className="h-4 w-4" />
                </div>
              ) : null}
            />
            {SUBSCALES.map(({ key, label, max }) => {
              const sub = results.subscales?.[key];
              if (!sub) {
                return (
                  <div key={key} data-scale-row={label} className="col-span-5 pl-9 pr-3 py-3 flex items-center justify-between gap-2">
                    <h3 className="font-medium text-gray-500 truncate">{label}</h3>
                    <span className="text-xs text-gray-500 italic flex-shrink-0">Not scored</span>
                  </div>
                );
              }
              return (
                <SubscaleRow
                  key={key}
                  label={label}
                  score={sub.score}
                  max={max}
                  severity={sub.severity}
                  indented
                />
              );
            })}
          </>
        ) : (
          <div className="col-span-5 px-3 py-3 bg-gray-50">
            <p className="text-xs text-gray-500 italic">{emptyText}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function snapResultsFromInformantView(view) {
  return snapResultsFrom(view);
}

export default function CypSnapScalesPanel({
  guardianView,
  schoolRepView,
  onViewGuardianDetails,
  onViewSchoolDetails,
}) {
  return (
    <div className="text-left w-full p-5 bg-white rounded-xl shadow border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Rating scales</h2>
      </div>
      <p className="text-left text-sm text-gray-600 mb-4">
        ADHD symptoms scale (SNAP-IV 26), rated over the past 6 months.
      </p>
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <CypSnapScaleColumn
            heading="Parent/carer"
            results={snapResultsFrom(guardianView)}
            emptyText="Not completed yet"
            onViewDetails={onViewGuardianDetails}
          />
        </div>
        <div className="flex-1 min-w-0">
          <CypSnapScaleColumn
            heading="School"
            results={snapResultsFrom(schoolRepView)}
            emptyText="School input not received"
            onViewDetails={onViewSchoolDetails}
          />
        </div>
      </div>
    </div>
  );
}
